import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Stack,
  Title,
  Skeleton,
  Text,
  Spacer,
  Alert,
  DescriptionList,
  Badge,
  Accordion,
  List,
  Menu,
  Tabs,
  Card,
  Icon,
  Divider
} from '@efast_public/fjd-component-library';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import {
  GovosApiRoutes,
  govosClient,
  invalidateConfiguredOnlinediensteCache,
  invalidatePaginationConfiguredOnlinediensteCache
} from '../api/govos-api';
import {
  GovOsResult,
  ManagedOnlineServiceWithDiff,
  OnlinedienstDiff,
  UpdateVerfahrenStateRequest,
  UserGroupResponse,
  Verfahren
} from '../api/govos.types';
import { RouteKey } from './AppRoutes';
import { EditPaymentConfigModal } from '../modals/EditPaymentConfigModal';
import { EditOnlinedienstMetadatenModal } from '../modals/EditOnlindienstMetadatenModal';
import { EditAuthenticationOptionsModal } from '../modals/EditAuthenticationOptionsModal';
import { EditSchriftformConfigModal } from '../modals/EditSchriftformConfigModal';
import { EditZustellwegConfigModal } from '../modals/EditZustellwegConfigModal';
import { useNotification } from '../shared/notification/useNotification';
import { createOnlinedienstTemplateUrl } from '../utils/odUtil';
import { EditZuFiConfigModal } from '../modals/EditZuFiConfigModal';
import { EditVerarbeitungsverzModal } from '../modals/EditVerarbeitungsverzModal';
import { EditUeberschreibbareFelderModal } from '../modals/EditUeberschreibbareFelderModal';
import { OriginalValuesModal } from '../modals/OriginalValuesModal';
import { TableMenuButton } from '../shared/components/TableMenuButton';
import { DiffDescriptionListItem } from '../shared/DiffDescriptionListItem';

function isStandardPrivacyText(privacyText?: string | null): boolean {
  return (
    privacyText === null || privacyText === undefined || privacyText === ''
  );
}
import { OdmAccordionGroup } from '../shared/OdmAccordionGroup';
import { UeberschreibbaresFeld } from '../api/govos.types';

type ConfigurationSection =
  | 'verfahren'
  | 'ueberschreibbareFelder'
  | 'auth'
  | 'schriftform'
  | 'vv'
  | 'payment'
  | 'zustellweg'
  | 'zustaendigkeitsfinder';

const mapOnlinedienstError = (errors: string[]) => {
  return errors.reduce(
    (acc, error) => {
      if (error.includes('Authentifikation') || error.includes('eID')) {
        acc.auth = true;
        acc.schriftform = true;
      }
      if (error.includes('Zustellweg') || error.includes('destination')) {
        acc.zustellweg = true;
      }
      if (error.includes('vorkonfiguriert')) {
        acc.ueberschreibbareFelder = true;
      } else if (error.includes('Verfahren')) {
        acc.verfahren = true;
      }
      if (error.includes('Bezahl')) {
        acc.payment = true;
      }
      if (
        error.includes('Überschreibbare') ||
        error.includes('überschreibbar')
      ) {
        acc.ueberschreibbareFelder = true;
      }
      return acc;
    },
    {} as Record<ConfigurationSection, boolean>
  );
};

const DIFF_DELIMITER = '→';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const hasDelimiterInObject = (obj: any): boolean => {
  if (obj === null || obj === undefined) {
    return false;
  }

  if (
    typeof obj === 'string' ||
    typeof obj === 'number' ||
    typeof obj === 'boolean'
  ) {
    return String(obj).includes(DIFF_DELIMITER);
  }

  if (Array.isArray(obj)) {
    return obj.some((item) => hasDelimiterInObject(item));
  }

  if (typeof obj === 'object') {
    return Object.values(obj).some((value) => hasDelimiterInObject(value));
  }

  return false;
};

const hasUeberschreibbareFelderDifference = (
  oldFelder: UeberschreibbaresFeld[] | undefined,
  newFelder: UeberschreibbaresFeld[] | undefined
): boolean => {
  if (!oldFelder && !newFelder) {
    return false;
  }
  if (!oldFelder || !newFelder) {
    return true;
  }

  const oldFelderMap = new Map(oldFelder.map((f) => [f.feldId, f]));
  const newFelderMap = new Map(newFelder.map((f) => [f.feldId, f]));

  if (oldFelderMap.size !== newFelderMap.size) {
    return true;
  }

  const entries = Array.from(newFelderMap.entries());
  for (const [feldId, newFeld] of entries) {
    const oldFeld = oldFelderMap.get(feldId);
    if (!oldFeld) {
      return true;
    }
    if (
      oldFeld.isOverwritten !== newFeld.isOverwritten ||
      JSON.stringify(oldFeld.ueberschriebeneWerte) !==
        JSON.stringify(newFeld.ueberschriebeneWerte)
    ) {
      return true;
    }
  }

  return false;
};

const mapVerfahrenDiff = (verfahren: Partial<Verfahren>) => {
  return {
    verfahren:
      hasDelimiterInObject(verfahren.metadata) ||
      hasDelimiterInObject(verfahren.quittung) ||
      hasDelimiterInObject(verfahren.templateId) ||
      hasDelimiterInObject(verfahren.fimVersionDatenschema) ||
      hasDelimiterInObject(verfahren.templateVersion),
    auth: hasDelimiterInObject({
      loginAsCitizen: verfahren.authentication?.loginAsCitizen,
      loginAsEmployee: verfahren.authentication?.loginAsEmployee,
      authLevel: verfahren.authentication?.authLevel,
      loginAsGroup: verfahren.authentication?.loginAsGroup,
      eidService: verfahren.authentication?.eidService
    }),
    schriftform: hasDelimiterInObject({
      schriftformNotRequired: verfahren.authentication?.schriftformNotRequired,
      withHybrid: verfahren.authentication?.withHybrid,
      withSignature: verfahren.authentication?.withSignature,
      withSignatureFormUpload:
        verfahren.authentication?.withSignatureFormUpload,
      withSignatureService: verfahren.authentication?.withSignatureService,
      withEid: verfahren.authentication?.withEid,
      withSubstantiel: verfahren.authentication?.withSubstantiel,
      withEidLogin: verfahren.authentication?.withEidLogin
    }),
    ueberschreibbareFelder: hasDelimiterInObject(
      verfahren.ueberschreibbareFelder
    ),
    payment: hasDelimiterInObject(verfahren.payment),
    zustellweg: hasDelimiterInObject(verfahren.zustellwege),
    zustaendigkeitsfinder: hasDelimiterInObject(verfahren.zufi),
    vv: false
  } as Record<ConfigurationSection, boolean>;
};

const compareUeberschreibbareFelder = (
  oldFelder: UeberschreibbaresFeld[] | undefined,
  newFelder: UeberschreibbaresFeld[] | undefined
): UeberschreibbaresFeld[] | undefined => {
  if (!oldFelder && !newFelder) {
    return undefined;
  }
  if (!oldFelder) {
    return newFelder;
  }
  if (!newFelder) {
    return oldFelder;
  }

  const oldFelderMap = new Map(oldFelder.map((f) => [f.feldId, f]));
  const newFelderMap = new Map(newFelder.map((f) => [f.feldId, f]));
  const allFeldIds = new Set([
    ...Array.from(oldFelderMap.keys()),
    ...Array.from(newFelderMap.keys())
  ]);

  const result: UeberschreibbaresFeld[] = [];

  allFeldIds.forEach((feldId) => {
    const oldFeld = oldFelderMap.get(feldId);
    const newFeld = newFelderMap.get(feldId);

    if (!oldFeld && newFeld) {
      result.push(newFeld);
    } else if (oldFeld && !newFeld) {
      result.push(oldFeld);
    } else if (oldFeld && newFeld) {
      result.push(newFeld);
    }
  });

  return result;
};

const compareVerfahren = (
  oldVerfahren: Verfahren,
  newVerfahren: Verfahren
): Partial<Verfahren> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any = {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const compareValues = (oldVal: any, newVal: any): any => {
    if (oldVal === newVal) {
      return oldVal;
    }

    if (Array.isArray(oldVal) && Array.isArray(newVal)) {
      if (JSON.stringify(oldVal) === JSON.stringify(newVal)) {
        return oldVal;
      }
      const maxLength = Math.max(oldVal.length, newVal.length);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resultArray: any[] = [];
      for (let i = 0; i < maxLength; i++) {
        resultArray.push(compareValues(oldVal[i], newVal[i]));
      }
      return resultArray;
    }

    if (Array.isArray(oldVal) && !Array.isArray(newVal)) {
      return oldVal;
    }

    if (!Array.isArray(oldVal) && Array.isArray(newVal)) {
      return newVal;
    }

    if (
      typeof oldVal === 'object' &&
      oldVal !== null &&
      typeof newVal === 'object' &&
      newVal !== null
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const nestedResult: any = {};
      const allKeys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)]);

      allKeys.forEach((key) => {
        nestedResult[key] = compareValues(oldVal[key], newVal[key]);
      });

      return nestedResult;
    }

    return `${oldVal} ${DIFF_DELIMITER} ${newVal}`;
  };

  const verfahrenKeys = Object.keys(newVerfahren) as Array<keyof Verfahren>;

  verfahrenKeys.forEach((key) => {
    if (key === 'ueberschreibbareFelder') {
      result[key] = compareUeberschreibbareFelder(
        oldVerfahren[key],
        newVerfahren[key]
      );
    } else {
      result[key] = compareValues(oldVerfahren[key], newVerfahren[key]);
    }
  });

  return result as Partial<Verfahren>;
};

const getVerfahrenDiffList = (
  oldVerfahren: Verfahren,
  newVerfahren: Verfahren
): OnlinedienstDiff[] => {
  const diffList: OnlinedienstDiff[] = [];

  const collectUeberschreibbareFelderDifferences = (
    oldFelder: UeberschreibbaresFeld[] | undefined,
    newFelder: UeberschreibbaresFeld[] | undefined
  ) => {
    if (!oldFelder && !newFelder) {
      return;
    }
    if (!oldFelder) {
      newFelder?.forEach((feld) => {
        diffList.push({
          topic: 'ueberschreibbareFelder',
          key: feld.feldName,
          oldVal: '-',
          newVal: 'added'
        });
      });
      return;
    }
    if (!newFelder) {
      oldFelder.forEach((feld) => {
        diffList.push({
          topic: 'ueberschreibbareFelder',
          key: feld.feldName,
          oldVal: 'removed',
          newVal: '-'
        });
      });
      return;
    }

    const oldFelderMap = new Map(oldFelder.map((f) => [f.feldId, f]));
    const newFelderMap = new Map(newFelder.map((f) => [f.feldId, f]));
    const allFeldIds = new Set([
      ...Array.from(oldFelderMap.keys()),
      ...Array.from(newFelderMap.keys())
    ]);

    allFeldIds.forEach((feldId) => {
      const oldFeld = oldFelderMap.get(feldId);
      const newFeld = newFelderMap.get(feldId);

      if (!oldFeld && newFeld) {
        diffList.push({
          topic: 'ueberschreibbareFelder',
          key: newFeld.feldName,
          oldVal: '-',
          newVal: 'added'
        });
      } else if (oldFeld && !newFeld) {
        diffList.push({
          topic: 'ueberschreibbareFelder',
          key: oldFeld.feldName,
          oldVal: 'removed',
          newVal: '-'
        });
      } else if (oldFeld && newFeld) {
        if (oldFeld.isOverwritten !== newFeld.isOverwritten) {
          const oldStatus = oldFeld.isOverwritten
            ? 'overwritten'
            : 'not overwritten';
          const newStatus = newFeld.isOverwritten
            ? 'overwritten'
            : 'not overwritten';
          diffList.push({
            topic: 'ueberschreibbareFelder',
            key: newFeld.feldName,
            oldVal: oldStatus,
            newVal: newStatus
          });
        }
        if (
          JSON.stringify(oldFeld.ueberschriebeneWerte) !==
          JSON.stringify(newFeld.ueberschriebeneWerte)
        ) {
          diffList.push({
            topic: 'ueberschreibbareFelder',
            key: `${newFeld.feldName} (values)`,
            oldVal: JSON.stringify(oldFeld.ueberschriebeneWerte),
            newVal: JSON.stringify(newFeld.ueberschriebeneWerte)
          });
        }
      }
    });
  };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const collectDifferences = (
    oldVal: any,
    newVal: any,
    /* eslint-enable @typescript-eslint/no-explicit-any */
    key: string,
    topic: string
  ) => {
    if (oldVal === newVal) {
      return;
    }

    if (Array.isArray(oldVal) && Array.isArray(newVal)) {
      if (JSON.stringify(oldVal) === JSON.stringify(newVal)) {
        return;
      }
      diffList.push({
        topic,
        key,
        oldVal: JSON.stringify(oldVal),
        newVal: JSON.stringify(newVal)
      });
      return;
    }

    if (
      typeof oldVal === 'object' &&
      oldVal !== null &&
      typeof newVal === 'object' &&
      newVal !== null
    ) {
      const allKeys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)]);

      allKeys.forEach((nestedKey) => {
        collectDifferences(
          oldVal[nestedKey],
          newVal[nestedKey],
          nestedKey,
          topic
        );
      });
      return;
    }

    diffList.push({
      topic,
      key,
      oldVal: String(oldVal ?? ''),
      newVal: String(newVal ?? '')
    });
  };

  const topicMapping: Record<string, string> = {
    isUoa: 'verfahren',
    attachLaterUrl: 'verfahren',
    isAttachLater: 'verfahren',
    isPrefillAvailable: 'verfahren',
    summary: 'verfahren',
    finalText: 'verfahren',
    hybridSubmissionText: 'verfahren',
    releaseNotes: 'verfahren',
    description: 'verfahren',
    antragsTyp: 'verfahren',
    active: 'verfahren',
    quittung: 'verfahren',
    title: 'verfahren',
    templateId: 'verfahren',
    isObjection: 'verfahren',
    fimVersionDatenschema: 'verfahren',
    encryption: 'verfahren',
    processId: 'verfahren',
    configId: 'verfahren',
    objectionUrl: 'verfahren',
    templateVersion: 'verfahren',
    onlinedienstId: 'verfahren',
    authentication: 'auth',
    payment: 'payment',
    zustellwege: 'zustellweg',
    schriftform: 'schriftform',
    zufi: 'zustaendigkeitsfinder',
    ueberschreibbareFelder: 'ueberschreibbareFelder'
  };

  const verfahrenKeys = Object.keys(newVerfahren) as Array<keyof Verfahren>;

  verfahrenKeys.forEach((key) => {
    if (key === 'ueberschreibbareFelder') {
      collectUeberschreibbareFelderDifferences(
        oldVerfahren[key],
        newVerfahren[key]
      );
    } else {
      const topic = topicMapping[key] || 'verfahren';
      collectDifferences(oldVerfahren[key], newVerfahren[key], key, topic);
    }
  });

  return diffList;
};

const mapOnlinedienstIncidents = (
  containerType: ContainerType,
  onlinedienst?: ManagedOnlineServiceWithDiff
): Record<ConfigurationSection, boolean> => {
  if (!onlinedienst) {
    return {} as Record<ConfigurationSection, boolean>;
  }
  if (containerType === 'diff') {
    const diff = mapVerfahrenDiff(onlinedienst?.diffContainer);
    // Check überschreibbare felder differences separately
    const hasUeberschreibbareFelderDiff = hasUeberschreibbareFelderDifference(
      onlinedienst.activeContainer.ueberschreibbareFelder,
      onlinedienst.editableContainer.ueberschreibbareFelder
    );
    return {
      ...diff,
      ueberschreibbareFelder: hasUeberschreibbareFelderDiff
    } as Record<ConfigurationSection, boolean>;
  }
  return mapOnlinedienstError(onlinedienst?.validation.errors ?? []);
};

const mapOdIncidentsToColor = (
  incident: boolean,
  containerType: ContainerType
) => {
  if (incident && containerType === 'edit') {
    return 'warning';
  }
  return undefined;
};

type ContainerType = 'edit' | 'active' | 'diff';

export function ManagedOnlinedienstDetailPage() {
  const { processId } = useParams<{ processId: string }>();
  const { t } = useTranslation();
  const { notifyUser, notifyUserFromRequest, confirmByUser } =
    useNotification();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedSection, setSelectedSection] =
    useState<ConfigurationSection | null>(null);
  const [selectedServiceIndex, setSelectedServiceIndex] = useState<
    number | null
  >(null);
  const [renderedOnlinedienst, setRenderedOnlinedienst] =
    useState<Verfahren | null>(null);
  const [selectedContainer, setSelectedContainer] =
    useState<ContainerType>('edit');
  const [shouldRedirectToTab, setShouldRedirectToTab] = useState<
    null | 'active' | 'edit'
  >(null);
  const [selectedFeld, setSelectedFeld] =
    useState<UeberschreibbaresFeld | null>(null);
  const [shouldShowOriginalValues, setShouldShowOriginalValues] =
    useState(false);

  const { data: onlinedienst, isLoading: isLoadingOnlineDienst } =
    useQuery<ManagedOnlineServiceWithDiff>({
      queryKey: [GovosApiRoutes.configuredOnlineDienst(processId ?? '')],
      select: (data) =>
        ({
          ...data,
          diffContainer: compareVerfahren(
            data.activeContainer,
            data.editableContainer
          ),
          diffList: getVerfahrenDiffList(
            data.activeContainer,
            data.editableContainer
          )
        }) as ManagedOnlineServiceWithDiff,
      enabled: !!processId
    });

  const startappid = onlinedienst?.startAppId ?? 0;

  useQuery<UserGroupResponse>({
    queryKey: [GovosApiRoutes.userGroups(startappid)],
    enabled: !!startappid
  });

  const validationIncidents = mapOnlinedienstIncidents(
    selectedContainer,
    onlinedienst
  );

  const clickOnTab = (tab: 'active' | 'edit') => {
    (
      document
        .getElementsByClassName(`odm-tab-clickable-${tab}`)
        ?.item(0) as HTMLButtonElement | null
    )?.click();
    setShouldRedirectToTab(null);
  };

  // After od data was refetched we can redirect the customer the corresponding Tab
  useEffect(() => {
    if (shouldRedirectToTab) {
      if (shouldRedirectToTab === 'active' && onlinedienst?.isActive === true) {
        clickOnTab(shouldRedirectToTab);
      }
      if (shouldRedirectToTab === 'edit' && onlinedienst?.isActive === false) {
        clickOnTab(shouldRedirectToTab);
      }
    }
  }, [shouldRedirectToTab, onlinedienst]);

  const { mutate: deleteOnlineDienst } = useMutation({
    mutationFn: async ({ processId }: { processId: number }) =>
      notifyUserFromRequest(
        () =>
          govosClient.delete<GovOsResult<unknown>>(
            GovosApiRoutes.configuredOnlineDienst(processId)
          ),
        {
          successMessage: t('ODOverviewPage.deleteSuccess'),
          errorMessage: t('ODOverviewPage.deleteError'),
          loadingMessage: t('ODOverviewPage.deleteLoading')
        }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: invalidatePaginationConfiguredOnlinediensteCache
      });
      navigate(RouteKey.MANAGED_ONLINEDIENSTE);
    }
  });

  const { mutate: updateOnlinedienstState, isPending: isUpdating } =
    useMutation({
      mutationFn: async (request: UpdateVerfahrenStateRequest) =>
        notifyUserFromRequest(
          () =>
            govosClient.put<GovOsResult<unknown>>(
              GovosApiRoutes.onlineServiceState(
                onlinedienst?.editableContainer.processId ?? processId ?? ''
              ),
              request
            ),
          {
            successMessage: t('ManagedOnlinedienstDetail.activateSuccess'),
            errorMessage: t('ManagedOnlinedienstDetail.activateError'),
            loadingMessage: t('ManagedOnlinedienstDetail.activateLoading')
          }
        ),
      onSuccess: (_, payload) => {
        void queryClient.refetchQueries({
          predicate: invalidatePaginationConfiguredOnlinediensteCache
        });
        void queryClient.invalidateQueries({
          predicate: invalidateConfiguredOnlinediensteCache(processId ?? '')
        });

        if (payload.shouldActivate) setShouldRedirectToTab('active');
        if (payload.shouldDeactivate) setShouldRedirectToTab('edit');
      }
    });

  const { mutate: deleteZustellweg, isPending: isDeletingZustellweg } =
    useMutation({
      mutationFn: async ({
        startAppId,
        processId,
        serviceIndex
      }: {
        startAppId: number;
        processId: number;
        serviceIndex: number;
      }) =>
        notifyUserFromRequest(
          () =>
            govosClient.delete<GovOsResult<unknown>>(
              GovosApiRoutes.deliveryService(startAppId, processId),
              { data: { serviceIndex } }
            ),
          {
            successMessage: t(
              'ManagedOnlinedienstDetail.deleteZustellwegSuccess'
            ),
            errorMessage: t('ManagedOnlinedienstDetail.deleteZustellwegError'),
            loadingMessage: t(
              'ManagedOnlinedienstDetail.deleteZustellwegLoading'
            )
          }
        ),
      onSuccess: () => {
        void queryClient.invalidateQueries({
          predicate: invalidateConfiguredOnlinediensteCache(processId ?? '')
        });
      }
    });

  const { mutate: resetDraft, isPending: isResettingDraft } = useMutation({
    mutationFn: async ({ processId }: { processId: number }) =>
      notifyUserFromRequest(
        () =>
          govosClient.post<GovOsResult<unknown>>(
            GovosApiRoutes.resetDraft(processId)
          ),
        {
          successMessage: t('ManagedOnlinedienstDetail.resetDraftSuccess'),
          errorMessage: t('ManagedOnlinedienstDetail.resetDraftError'),
          loadingMessage: t('ManagedOnlinedienstDetail.resetDraftLoading')
        }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: invalidateConfiguredOnlinediensteCache(processId ?? '')
      });
    }
  });

  const handleActivateClick = () => {
    let title = t('ManagedOnlinedienstDetail.overrideTitle');
    let content = t('ManagedOnlinedienstDetail.overrideMessage');
    if (onlinedienst && !onlinedienst.isActive) {
      title = t('ManagedOnlinedienstDetail.activateTitle');
      content = t('ManagedOnlinedienstDetail.activateMessage');
    }

    confirmByUser({
      title,
      content,
      callback: () => {
        updateOnlinedienstState({ shouldActivate: true });
      }
    });
  };

  const handleDeactivateClick = () => {
    confirmByUser({
      title: t('ManagedOnlinedienstDetail.deactivateTitle'),
      content: t('ManagedOnlinedienstDetail.deactivateMessage'),
      callback: () => updateOnlinedienstState({ shouldDeactivate: true })
    });
  };

  const handleResetDraftClick = () => {
    if (!onlinedienst) {
      return;
    }
    confirmByUser({
      title: t('ManagedOnlinedienstDetail.resetDraftTitle'),
      content: t('ManagedOnlinedienstDetail.resetDraftMessage'),
      callback: () =>
        resetDraft({ processId: onlinedienst.editableContainer.processId })
    });
  };

  const handleCloseModal = () => {
    setSelectedSection(null);
  };

  const formatBool = (val: boolean) =>
    val
      ? t('ManagedOnlinedienstDetail.verfahren.activeYes')
      : t('ManagedOnlinedienstDetail.verfahren.activeNo');

  const getStatusText = (
    isOptional: boolean,
    isOverwritten: boolean
  ): string => {
    if (isOptional && !isOverwritten)
      return t(
        'ManagedOnlinedienstDetail.ueberschreibbareFelder.status.nichtUeberschriebenOptional'
      );
    if (isOptional && isOverwritten)
      return t(
        'ManagedOnlinedienstDetail.ueberschreibbareFelder.status.ueberschriebenOptional'
      );
    if (!isOptional && !isOverwritten)
      return t(
        'ManagedOnlinedienstDetail.ueberschreibbareFelder.status.nichtUeberschriebenVerpflichtend'
      );
    return t(
      'ManagedOnlinedienstDetail.ueberschreibbareFelder.status.ueberschriebenVerpflichtend'
    );
  };

  const getIconProps = (isOptional: boolean, isOverwritten: boolean) => ({
    icon: (!isOptional && !isOverwritten
      ? 'error-filled'
      : isOptional && !isOverwritten
        ? 'checkmark-outline'
        : 'checkmark-filled') as
      | 'error-filled'
      | 'checkmark-outline'
      | 'checkmark-filled',
    color: (!isOptional && !isOverwritten ? 'danger-600' : 'success-600') as
      | 'danger-600'
      | 'success-600'
  });

  const diffMapper = (
    key: string,
    value: string,
    actualRecord: object | undefined,
    editableRecord: object | undefined,
    areSameKeys: boolean,
    shouldReturnActive: boolean
  ) => {
    if (selectedContainer === 'diff' && !areSameKeys) {
      if (shouldReturnActive && actualRecord) {
        const ac = actualRecord as Record<string, string>;
        return [ac[key] || '-', '-'];
      }

      const ed = editableRecord as Record<string, string>;
      return ['-', ed[key] || '-'];
    }

    if (selectedContainer === 'diff' && actualRecord && editableRecord) {
      const ac = actualRecord as Record<string, string>;
      const ed = editableRecord as Record<string, string>;
      if (areSameKeys) {
        const activeValue = ac[key] || '-';
        const editableValue = ed[key] || '-';
        return [activeValue, editableValue];
      }
    }

    return [value || '-'];
  };

  const handleContainerChange = (container: ContainerType) => {
    if (!onlinedienst) {
      return;
    }

    if (container === 'active') {
      setRenderedOnlinedienst(onlinedienst.activeContainer);
    } else if (container === 'edit') {
      setRenderedOnlinedienst(onlinedienst.editableContainer);
    } else if (container === 'diff') {
      setRenderedOnlinedienst(onlinedienst.diffContainer);
    }
  };

  const handleTabChange = (tab: ContainerType) => {
    setSelectedContainer(tab);
    handleContainerChange(tab);
  };

  useEffect(() => {
    if (onlinedienst) {
      handleContainerChange(selectedContainer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlinedienst]);

  if (isLoadingOnlineDienst && !onlinedienst) {
    return (
      <Stack spacing="xxl" orientation="vertical">
        <Skeleton height={40} />
        <Skeleton height={200} />
        <Skeleton height={300} />
      </Stack>
    );
  }

  if (!onlinedienst || !startappid || !renderedOnlinedienst) {
    return (
      <Stack spacing="xxxl" direction="column">
        <Title level="2">{t('ManagedOnlinedienstDetail.notFound')}</Title>
        <Button
          variant="secondary-outline"
          onClick={() => navigate(RouteKey.MANAGED_ONLINEDIENSTE)}
        >
          {t('ManagedOnlinedienstDetail.backButton')}
        </Button>
      </Stack>
    );
  }

  const hasUpperSectionDiff =
    selectedContainer !== 'diff' ||
    onlinedienst.activeContainer.leikaId !==
      onlinedienst.editableContainer.leikaId ||
    onlinedienst.activeContainer.fimVersionDatenschema !==
      onlinedienst.editableContainer.fimVersionDatenschema ||
    onlinedienst.activeContainer.urn !== onlinedienst.editableContainer.urn;

  return (
    <div
      style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}
    >
      <Stack spacing="xxl" orientation="vertical">
        <Stack direction="row" justifyContent="space-between" spacing="xl">
          <Stack direction="row" spacing="xl">
            <Button
              variant="primary-text"
              beforeIcon="arrow-left"
              onClick={() => navigate(RouteKey.MANAGED_ONLINEDIENSTE)}
            >
              {t('ManagedOnlinedienstDetail.shortBackButton')}
            </Button>
            <Title level="4">
              {onlinedienst.editableContainer.metadata.title}
            </Title>
          </Stack>
          <Stack direction="row" spacing="xs">
            <Button
              variant="primary"
              beforeIcon="play"
              disabled={
                onlinedienst.validation.errors.length > 0 ||
                onlinedienst.validation.warnings.length > 0 ||
                isUpdating ||
                !onlinedienst.validation.canActivate ||
                (!onlinedienst.validation.hasConfigurationChanges &&
                  onlinedienst.isActive)
              }
              onClick={handleActivateClick}
            >
              {onlinedienst.isActive
                ? t('ManagedOnlinedienstDetail.overwriteButton')
                : t('ManagedOnlinedienstDetail.activateButton')}
            </Button>

            <TableMenuButton variant="secondary-outline" autoWidth={true}>
              <Menu.MenuItem
                icon="trashcan"
                label="Online-Dienst löschen"
                onClick={() => {
                  confirmByUser({
                    title: t('ManagedOnlinedienstDetail.deleteTitle'),
                    content: t('ManagedOnlinedienstDetail.deleteMessage'),
                    callback: () =>
                      deleteOnlineDienst({
                        processId: renderedOnlinedienst.processId
                      })
                  });
                }}
              />
              {onlinedienst.isActive ? (
                <Menu.MenuItem
                  icon="pause"
                  label={t('ManagedOnlinedienstDetail.deactivateButton')}
                  onClick={handleDeactivateClick}
                />
              ) : (
                <div />
              )}
              <Divider />
              <Menu.MenuItem
                icon="reset"
                label={t('ManagedOnlinedienstDetail.resetDraftButton')}
                onClick={handleResetDraftClick}
                disabled={isResettingDraft}
              />
            </TableMenuButton>
          </Stack>
        </Stack>

        {/* Alert */}
        {(onlinedienst.validation.errors.length > 0 ||
          onlinedienst.validation.warnings.length > 0) && (
          <Alert
            message={
              <>
                <Text size="m">
                  {t('ManagedOnlinedienstDetail.alert.notConfigured')}
                </Text>
                <Spacer size="xs" />
                <List type="bullet">
                  {onlinedienst.validation.errors.map((error) => (
                    <List.ListItem key={error}>{error}</List.ListItem>
                  ))}
                </List>
              </>
            }
            variant="warning"
          />
        )}

        {/* Drawer Layout with Sidebar and Content */}
        <div
          style={{
            display: 'flex',
            position: 'relative'
          }}
        >
          {/* Main Content - Online-Dienst Details */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              marginTop: '24px',
              paddingBottom: '36px'
            }}
          >
            <Tabs
              style={{
                flex: 1
              }}
              index={1}
            >
              <Tabs.List>
                <Tabs.Tab
                  className="odm-tab-clickable-active"
                  disabled={!onlinedienst.isActive}
                  onClick={() => {
                    if (!onlinedienst.isActive) {
                      (
                        document
                          .getElementsByClassName('odm-tab-clickable')
                          ?.item(0) as HTMLButtonElement | null
                      )?.click();
                      confirmByUser({
                        title: t('ManagedOnlinedienstDetail.activateTitle'),
                        content: t('ManagedOnlinedienstDetail.activateMessage'),
                        callback: () => {}
                      });
                    } else {
                      handleTabChange('active');
                    }
                  }}
                  icon="play"
                  key="active"
                  label={t('ManagedOnlinedienstDetail.sidebar.activeConfig')}
                />
                <Tabs.Tab
                  className="odm-tab-clickable-edit"
                  onClick={() => handleTabChange('edit')}
                  icon="edit"
                  key="edit"
                  label={t('ManagedOnlinedienstDetail.sidebar.editConfig', {
                    value: onlinedienst.validation.hasConfigurationChanges
                      ? `(bearbeitet)`
                      : ''
                  })}
                />
                <Tabs.Tab
                  disabled={
                    !onlinedienst.isActive ||
                    !onlinedienst.validation.hasConfigurationChanges
                  }
                  onClick={() => {
                    if (!onlinedienst.isActive) {
                      (
                        document
                          .getElementsByClassName('odm-tab-clickable')
                          ?.item(0) as HTMLButtonElement | null
                      )?.click();
                      confirmByUser({
                        title: t('ManagedOnlinedienstDetail.activateTitle'),
                        content: t('ManagedOnlinedienstDetail.activateMessage'),
                        callback: () => {}
                      });
                    } else {
                      handleTabChange('diff');
                    }
                  }}
                  icon="arrows-horizontal"
                  key="diff"
                  label={t('ManagedOnlinedienstDetail.sidebar.split')}
                />
              </Tabs.List>
              <Tabs.Panels>
                <Tabs.Panel />
                <Tabs.Panel />
                <Tabs.Panel />
              </Tabs.Panels>
            </Tabs>
            <Stack spacing="5xl" orientation="vertical">
              {/* Online-Dienst Details */}
              {hasUpperSectionDiff && (
                <div style={{ marginTop: '36px' }}>
                  {selectedContainer !== 'diff' && (
                    <Title level="4" style={{ marginBottom: '16px' }}>
                      {t('ManagedOnlinedienstDetail.detailsTitle')}
                    </Title>
                  )}
                  <DescriptionList>
                    {selectedContainer === 'diff' ? (
                      <DiffDescriptionListItem
                        hasDivider={false}
                        key="desc"
                        title={<div style={{ width: '18vw' }} />}
                        values={['Aktive Konfiguration', 'Entwurf']}
                        valueRenderer={(value) => (
                          <Text underline strong>
                            {value}
                          </Text>
                        )}
                      />
                    ) : (
                      <React.Fragment key="emptyDesc" />
                    )}
                    <DiffDescriptionListItem
                      hasDivider={false}
                      key="status"
                      title={t('ManagedOnlinedienstDetail.fields.status')}
                      values={selectedContainer === 'diff' ? [] : ['online']}
                      valueRenderer={() =>
                        onlinedienst.isActive ? (
                          <Badge variant="success">Online</Badge>
                        ) : (
                          <Badge variant="warning">Offline</Badge>
                        )
                      }
                    />
                    <DiffDescriptionListItem
                      hasDivider
                      key="version"
                      title={t('ManagedOnlinedienstDetail.fields.version')}
                      values={
                        selectedContainer === 'diff'
                          ? []
                          : [renderedOnlinedienst.templateVersion]
                      }
                      valueRenderer={() => (
                        <Stack
                          direction="row"
                          spacing="xxs"
                          justifyContent="space-between"
                        >
                          <div style={{ alignContent: 'center' }}>
                            {renderedOnlinedienst?.templateVersion}
                          </div>
                          {onlinedienst.latestVersion !==
                            onlinedienst.editableContainer.templateVersion && (
                            <Button
                              variant="secondary-outline"
                              size="sm"
                              onClick={() =>
                                updateOnlinedienstState({
                                  updateToVersion: onlinedienst.latestVersion
                                })
                              }
                              beforeIcon="information-filled"
                            >
                              {t('ManagedOnlinedienstDetail.versionControl', {
                                version: onlinedienst.latestVersion
                              })}
                            </Button>
                          )}
                        </Stack>
                      )}
                    />
                    <DiffDescriptionListItem
                      key="url"
                      title={t('ManagedOnlinedienstDetail.fields.url')}
                      values={
                        selectedContainer === 'diff'
                          ? []
                          : [String(renderedOnlinedienst.processId)]
                      }
                      valueRenderer={() => (
                        <Stack direction="row" spacing="xxl">
                          <Button
                            size="sm"
                            variant="primary-text"
                            disabled={!onlinedienst.isActive}
                            afterIcon="arrow-up-right"
                            onClick={() => {
                              window.open(
                                createOnlinedienstTemplateUrl(
                                  onlinedienst?.processId ?? '',
                                  startappid
                                ),
                                '_blank'
                              );
                            }}
                          >
                            {t('ManagedOnlinedienstDetail.invokeOd')}
                          </Button>
                          <Button
                            size="sm"
                            variant="primary-text"
                            afterIcon="copy"
                            disabled={!onlinedienst.isActive}
                            onClick={() => {
                              notifyUser({
                                title: t(
                                  'ManagedOnlinedienstDetail.copyUrlSuccess'
                                ),
                                intent: 'success'
                              });
                              navigator.clipboard
                                .writeText(
                                  createOnlinedienstTemplateUrl(
                                    onlinedienst?.processId ?? '',
                                    startappid
                                  )
                                )
                                .catch(console.error);
                            }}
                          >
                            {t('ManagedOnlinedienstDetail.copyUrl')}
                          </Button>
                        </Stack>
                      )}
                    />
                    <DiffDescriptionListItem
                      key="leikaId"
                      title={t('ManagedOnlinedienstDetail.fields.leikaId')}
                      values={
                        selectedContainer === 'diff'
                          ? [
                              onlinedienst.activeContainer.leikaId || '-',
                              onlinedienst.editableContainer.leikaId || '-'
                            ]
                          : [String(renderedOnlinedienst.leikaId) || '-']
                      }
                    />
                    <DiffDescriptionListItem
                      key="fimSchemaId"
                      title={t('ManagedOnlinedienstDetail.fields.fimSchemaId')}
                      values={
                        selectedContainer === 'diff'
                          ? [
                              onlinedienst.activeContainer
                                .fimVersionDatenschema || '-',
                              onlinedienst.editableContainer
                                .fimVersionDatenschema || '-'
                            ]
                          : [String(renderedOnlinedienst.fimVersionDatenschema)]
                      }
                      valueRenderer={(value) =>
                        selectedContainer !== 'diff' ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              gap: '32px',
                              alignItems: 'baseline'
                            }}
                          >
                            <Text>{value}</Text>
                            <Button
                              size="sm"
                              variant="primary-text"
                              afterIcon="copy"
                              onClick={() => {
                                notifyUser({
                                  title: t(
                                    'ManagedOnlinedienstDetail.copyFimSchemaIdSuccess'
                                  ),
                                  intent: 'success'
                                });
                                navigator.clipboard
                                  .writeText(
                                    String(
                                      renderedOnlinedienst.fimVersionDatenschema
                                    )
                                  )
                                  .catch(console.error);
                              }}
                            >
                              {t('ManagedOnlinedienstDetail.copyFimSchemaId')}
                            </Button>
                          </span>
                        ) : undefined
                      }
                    />
                    <DiffDescriptionListItem
                      key="urn"
                      title={t('ManagedOnlinedienstDetail.fields.urn')}
                      values={
                        selectedContainer === 'diff'
                          ? [
                              onlinedienst.activeContainer.urn || '-',
                              onlinedienst.editableContainer.urn || '-'
                            ]
                          : [renderedOnlinedienst.urn]
                      }
                      valueRenderer={(value) =>
                        selectedContainer !== 'diff' ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              gap: '32px',
                              alignItems: 'baseline'
                            }}
                          >
                            <Text>{value}</Text>
                            <Button
                              size="sm"
                              variant="primary-text"
                              afterIcon="copy"
                              onClick={() => {
                                notifyUser({
                                  title: t(
                                    'ManagedOnlinedienstDetail.copyUrnSuccess'
                                  ),
                                  intent: 'success'
                                });
                                navigator.clipboard
                                  .writeText(renderedOnlinedienst.urn)
                                  .catch(console.error);
                              }}
                            >
                              {t('ManagedOnlinedienstDetail.copyUrn')}
                            </Button>
                          </span>
                        ) : undefined
                      }
                    />
                  </DescriptionList>
                </div>
              )}

              {/* Configuration Parameters */}
              <div>
                {selectedContainer !== 'diff' && (
                  <Title level="4" style={{ marginBottom: '16px' }}>
                    {t('ManagedOnlinedienstDetail.configParamsTitle')}
                  </Title>
                )}
                {selectedContainer === 'diff' && (
                  <DescriptionList style={{ marginBottom: '8px' }}>
                    <DiffDescriptionListItem
                      hasDivider={false}
                      key="accordion-diff-header"
                      title={<div style={{ width: '15vw' }} />}
                      values={['Aktive Konfiguration', 'Entwurf']}
                      valueRenderer={(value) => (
                        <Text underline strong>
                          {value}
                        </Text>
                      )}
                    />
                  </DescriptionList>
                )}
                <OdmAccordionGroup
                  allAccordionsExpandable={selectedContainer === 'diff'}
                >
                  {(selectedContainer !== 'diff' ||
                    validationIncidents['verfahren']) && (
                    <Accordion
                      variant={mapOdIncidentsToColor(
                        validationIncidents.verfahren,
                        selectedContainer
                      )}
                      expanded={
                        selectedContainer === 'diff' &&
                        validationIncidents['verfahren']
                      }
                      label={t('ManagedOnlinedienstDetail.config.verfahren')}
                      after={
                        selectedContainer === 'edit' && (
                          <Button
                            variant="primary-text"
                            beforeIcon="edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSection('verfahren');
                            }}
                            size="sm"
                          >
                            {t('ManagedOnlinedienstDetail.editButton')}
                          </Button>
                        )
                      }
                    >
                      <DescriptionList>
                        <DiffDescriptionListItem
                          hasDivider={false}
                          key="onlinedienstId"
                          title={t(
                            'ElementOnlinedienstMetadatenEditModal.fields.kennung'
                          )}
                          values={
                            selectedContainer === 'diff'
                              ? [
                                  String(
                                    onlinedienst.activeContainer.processId ||
                                      '-'
                                  ),
                                  String(
                                    onlinedienst.editableContainer.processId ||
                                      '-'
                                  )
                                ]
                              : [String(renderedOnlinedienst.processId || '-')]
                          }
                        />
                        <DiffDescriptionListItem
                          key="verfahren.title"
                          title={t(
                            'ElementOnlinedienstMetadatenEditModal.fields.titel'
                          )}
                          values={
                            selectedContainer === 'diff'
                              ? [
                                  onlinedienst.activeContainer.metadata.title,
                                  onlinedienst.editableContainer.metadata.title
                                ]
                              : [renderedOnlinedienst.metadata.title]
                          }
                        />
                        <DiffDescriptionListItem
                          key="verfahren.description"
                          title={t(
                            'ElementOnlinedienstMetadatenEditModal.fields.beschreibung'
                          )}
                          values={
                            selectedContainer === 'diff'
                              ? [
                                  onlinedienst.activeContainer.metadata
                                    .description || '-',
                                  onlinedienst.editableContainer.metadata
                                    .description || '-'
                                ]
                              : [
                                  renderedOnlinedienst.metadata.description ||
                                    '-'
                                ]
                          }
                        />
                        <DiffDescriptionListItem
                          key="verfahren.antragsTyp"
                          title={t(
                            'ElementOnlinedienstMetadatenEditModal.fields.typ'
                          )}
                          values={
                            selectedContainer === 'diff'
                              ? [
                                  onlinedienst.activeContainer.metadata
                                    .antragsTyp || '-',
                                  onlinedienst.editableContainer.metadata
                                    .antragsTyp || '-'
                                ]
                              : [
                                  renderedOnlinedienst.metadata.antragsTyp ||
                                    '-'
                                ]
                          }
                        />
                        <DiffDescriptionListItem
                          key="templateId"
                          title={t(
                            'ManagedOnlinedienstDetail.verfahren.templateId'
                          )}
                          values={
                            selectedContainer === 'diff'
                              ? [
                                  onlinedienst.activeContainer.templateId ||
                                    '-',
                                  onlinedienst.editableContainer.templateId ||
                                    '-'
                                ]
                              : [renderedOnlinedienst.templateId || '-']
                          }
                        />
                        <DiffDescriptionListItem
                          key="templateVersion"
                          title={t(
                            'ManagedOnlinedienstDetail.verfahren.templateVersion'
                          )}
                          values={
                            selectedContainer === 'diff'
                              ? [
                                  onlinedienst.activeContainer
                                    .templateVersion || '-',
                                  onlinedienst.editableContainer
                                    .templateVersion || '-'
                                ]
                              : [renderedOnlinedienst.templateVersion || '-']
                          }
                        />
                        <DiffDescriptionListItem
                          key="verfahren.encryption"
                          title={t(
                            'ElementOnlinedienstMetadatenEditModal.fields.verschluesselung'
                          )}
                          values={
                            selectedContainer === 'diff'
                              ? [
                                  onlinedienst.activeContainer.metadata
                                    .encryption.enabled
                                    ? t(
                                        'ManagedOnlinedienstDetail.verfahren.encryptionEnabled'
                                      )
                                    : t(
                                        'ManagedOnlinedienstDetail.verfahren.encryptionDisabled'
                                      ),
                                  onlinedienst.editableContainer.metadata
                                    .encryption.enabled
                                    ? t(
                                        'ManagedOnlinedienstDetail.verfahren.encryptionEnabled'
                                      )
                                    : t(
                                        'ManagedOnlinedienstDetail.verfahren.encryptionDisabled'
                                      )
                                ]
                              : [
                                  renderedOnlinedienst.metadata.encryption
                                    .enabled
                                    ? t(
                                        'ManagedOnlinedienstDetail.verfahren.encryptionEnabled'
                                      )
                                    : t(
                                        'ManagedOnlinedienstDetail.verfahren.encryptionDisabled'
                                      )
                                ]
                          }
                        />
                        <DiffDescriptionListItem
                          key="verfahren.isPrefillAvailable"
                          title={t(
                            'ElementOnlinedienstMetadatenEditModal.fields.vorbefuellung'
                          )}
                          values={
                            selectedContainer === 'diff'
                              ? [
                                  formatBool(
                                    onlinedienst.activeContainer.metadata
                                      .isPrefillAvailable
                                  ),
                                  formatBool(
                                    onlinedienst.editableContainer.metadata
                                      .isPrefillAvailable
                                  )
                                ]
                              : [
                                  formatBool(
                                    renderedOnlinedienst.metadata
                                      .isPrefillAvailable
                                  )
                                ]
                          }
                        />
                        <DiffDescriptionListItem
                          key="verfahren.summary"
                          title={t(
                            'ElementOnlinedienstMetadatenEditModal.fields.zusammenfassung'
                          )}
                          values={
                            selectedContainer === 'diff'
                              ? [
                                  onlinedienst.activeContainer.metadata
                                    .summary || '-',
                                  onlinedienst.editableContainer.metadata
                                    .summary || '-'
                                ]
                              : [renderedOnlinedienst.metadata.summary || '-']
                          }
                        />
                        <DiffDescriptionListItem
                          key="verfahren.hybridSubmissionText"
                          title={t(
                            'ElementOnlinedienstMetadatenEditModal.fields.textHybrideinreichung'
                          )}
                          values={
                            selectedContainer === 'diff'
                              ? [
                                  onlinedienst.activeContainer.metadata
                                    .hybridSubmissionText || '-',
                                  onlinedienst.editableContainer.metadata
                                    .hybridSubmissionText || '-'
                                ]
                              : [
                                  renderedOnlinedienst.metadata
                                    .hybridSubmissionText || '-'
                                ]
                          }
                        />
                        <DiffDescriptionListItem
                          key="verfahren.finalText"
                          title={t(
                            'ElementOnlinedienstMetadatenEditModal.fields.abschlusstext'
                          )}
                          values={
                            selectedContainer === 'diff'
                              ? [
                                  onlinedienst.activeContainer.metadata
                                    .finalText || '-',
                                  onlinedienst.editableContainer.metadata
                                    .finalText || '-'
                                ]
                              : [renderedOnlinedienst.metadata.finalText || '-']
                          }
                        />
                      </DescriptionList>
                    </Accordion>
                  )}

                  {((selectedContainer === 'diff'
                    ? onlinedienst.editableContainer
                    : renderedOnlinedienst
                  ).ueberschreibbareFelder?.length ?? 0) > 0 &&
                    (selectedContainer !== 'diff' ||
                      validationIncidents['ueberschreibbareFelder']) && (
                      <Accordion
                        variant={mapOdIncidentsToColor(
                          validationIncidents.ueberschreibbareFelder,
                          selectedContainer
                        )}
                        expanded={
                          selectedContainer === 'diff' &&
                          validationIncidents['ueberschreibbareFelder']
                        }
                        label={t(
                          'ManagedOnlinedienstDetail.config.ueberschreibbareFelder'
                        )}
                      >
                        <DescriptionList>
                          <>
                            {(selectedContainer === 'diff'
                              ? onlinedienst.editableContainer
                              : renderedOnlinedienst
                            ).ueberschreibbareFelder?.map((feld) => {
                              const isOptional =
                                feld.werteUeberschreibbarArt === 'OPTIONAL';

                              if (selectedContainer === 'diff') {
                                const oldFeld =
                                  onlinedienst.activeContainer.ueberschreibbareFelder?.find(
                                    (f) => f.feldId === feld.feldId
                                  );
                                const newFeld =
                                  onlinedienst.editableContainer.ueberschreibbareFelder?.find(
                                    (f) => f.feldId === feld.feldId
                                  );
                                const oldIconProps = getIconProps(
                                  isOptional,
                                  oldFeld?.isOverwritten ?? false
                                );
                                const newIconProps = getIconProps(
                                  isOptional,
                                  newFeld?.isOverwritten ?? false
                                );
                                const oldStatusText = getStatusText(
                                  isOptional,
                                  oldFeld?.isOverwritten ?? false
                                );
                                const newStatusText = getStatusText(
                                  isOptional,
                                  newFeld?.isOverwritten ?? false
                                );
                                const iconMap = new Map([
                                  [oldStatusText, oldIconProps],
                                  [newStatusText, newIconProps]
                                ]);
                                return (
                                  <DiffDescriptionListItem
                                    key={feld.feldId}
                                    title={
                                      <div
                                        style={{
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          maxWidth: '20vw'
                                        }}
                                        title={feld.feldName}
                                      >
                                        {feld.feldName}
                                      </div>
                                    }
                                    values={[oldStatusText, newStatusText]}
                                    valueRenderer={(value) => {
                                      const iconProps =
                                        iconMap.get(value) ?? oldIconProps;
                                      return (
                                        <Stack
                                          direction="row"
                                          spacing="s"
                                          justifyItems="center"
                                        >
                                          <Icon
                                            name={iconProps.icon}
                                            color={iconProps.color}
                                            title=""
                                          />
                                          <Text size="s">{value}</Text>
                                        </Stack>
                                      );
                                    }}
                                  />
                                );
                              }

                              const isOverwritten = feld.isOverwritten;
                              const statusText = getStatusText(
                                isOptional,
                                isOverwritten
                              );
                              const { icon: statusIcon, color } = getIconProps(
                                isOptional,
                                isOverwritten
                              );

                              return (
                                <DiffDescriptionListItem
                                  key={feld.feldId}
                                  title={
                                    <div
                                      style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '20vw'
                                      }}
                                      title={feld.feldName}
                                    >
                                      {feld.feldName}
                                    </div>
                                  }
                                  values={[statusText]}
                                  valueRenderer={(value) => (
                                    <Stack
                                      direction="row"
                                      spacing="s"
                                      justifyContent="space-between"
                                      alignItems="center"
                                    >
                                      <Stack
                                        direction="row"
                                        spacing="s"
                                        justifyItems="center"
                                      >
                                        <Icon
                                          name={statusIcon}
                                          color={color}
                                          title=""
                                        />
                                        <Text size="s">{value}</Text>
                                      </Stack>
                                      {selectedContainer === 'edit' && (
                                        <Button
                                          variant="primary-text"
                                          beforeIcon="edit"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedFeld(feld);
                                            setSelectedSection(
                                              'ueberschreibbareFelder'
                                            );
                                          }}
                                          size="sm"
                                        >
                                          {t(
                                            'ManagedOnlinedienstDetail.editButton'
                                          )}
                                        </Button>
                                      )}
                                    </Stack>
                                  )}
                                />
                              );
                            })}
                          </>
                        </DescriptionList>
                      </Accordion>
                    )}

                  {(selectedContainer !== 'diff' ||
                    validationIncidents['auth']) && (
                    <Accordion
                      variant={mapOdIncidentsToColor(
                        validationIncidents.auth,
                        selectedContainer
                      )}
                      expanded={
                        selectedContainer === 'diff' &&
                        validationIncidents['auth']
                      }
                      label={t('ManagedOnlinedienstDetail.config.authOptions')}
                      after={
                        selectedContainer === 'edit' && (
                          <Button
                            variant="primary-text"
                            beforeIcon="edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSection('auth');
                            }}
                            size="sm"
                          >
                            {t('ManagedOnlinedienstDetail.editButton')}
                          </Button>
                        )
                      }
                    >
                      <DescriptionList>
                        <DiffDescriptionListItem
                          hasDivider={false}
                          key="konto"
                          title={t('ManagedOnlinedienstDetail.auth.konto')}
                          values={
                            selectedContainer === 'diff'
                              ? [
                                  [
                                    onlinedienst.activeContainer.authentication
                                      .loginAsCitizen &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.buergerkonto'
                                      ),
                                    onlinedienst.activeContainer.authentication
                                      .loginAsEmployee &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.organisationskonto'
                                      )
                                  ]
                                    .filter(Boolean)
                                    .join(', ') || '-',
                                  [
                                    onlinedienst.editableContainer
                                      .authentication.loginAsCitizen &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.buergerkonto'
                                      ),
                                    onlinedienst.editableContainer
                                      .authentication.loginAsEmployee &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.organisationskonto'
                                      )
                                  ]
                                    .filter(Boolean)
                                    .join(', ') || '-'
                                ]
                              : [
                                  [
                                    renderedOnlinedienst.authentication
                                      .loginAsCitizen &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.buergerkonto'
                                      ),
                                    renderedOnlinedienst.authentication
                                      .loginAsEmployee &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.organisationskonto'
                                      )
                                  ]
                                    .filter(Boolean)
                                    .join(', ') || '-'
                                ]
                          }
                        />
                        <DiffDescriptionListItem
                          key="vertrauensniveau"
                          title={t(
                            'ManagedOnlinedienstDetail.auth.vertrauensniveau'
                          )}
                          values={
                            selectedContainer === 'diff'
                              ? [
                                  t(
                                    `ManagedOnlinedienstDetail.auth.authLevel.${onlinedienst.activeContainer.authentication.authLevel}`
                                  ),
                                  t(
                                    `ManagedOnlinedienstDetail.auth.authLevel.${onlinedienst.editableContainer.authentication.authLevel}`
                                  )
                                ]
                              : [
                                  t(
                                    `ManagedOnlinedienstDetail.auth.authLevel.${renderedOnlinedienst.authentication.authLevel}`
                                  )
                                ]
                          }
                        />
                        <DiffDescriptionListItem
                          key="privacyText"
                          title={t(
                            'EditAuthenticationOptionsModal.sections.datenschutz'
                          )}
                          values={
                            selectedContainer === 'diff'
                              ? [
                                  !isStandardPrivacyText(
                                    onlinedienst.activeContainer.authentication
                                      .privacyText
                                  )
                                    ? t(
                                        'EditAuthenticationOptionsModal.fields.customPrivacyText'
                                      )
                                    : t(
                                        'EditAuthenticationOptionsModal.fields.standardPrivacyText'
                                      ),
                                  !isStandardPrivacyText(
                                    onlinedienst.editableContainer
                                      .authentication.privacyText
                                  )
                                    ? t(
                                        'EditAuthenticationOptionsModal.fields.customPrivacyText'
                                      )
                                    : t(
                                        'EditAuthenticationOptionsModal.fields.standardPrivacyText'
                                      )
                                ]
                              : [
                                  !isStandardPrivacyText(
                                    renderedOnlinedienst.authentication
                                      .privacyText
                                  )
                                    ? t(
                                        'EditAuthenticationOptionsModal.fields.customPrivacyText'
                                      )
                                    : t(
                                        'EditAuthenticationOptionsModal.fields.standardPrivacyText'
                                      )
                                ]
                          }
                        />
                      </DescriptionList>
                    </Accordion>
                  )}

                  {(selectedContainer !== 'diff' ||
                    validationIncidents['schriftform']) && (
                    <Accordion
                      variant={mapOdIncidentsToColor(
                        validationIncidents.schriftform,
                        selectedContainer
                      )}
                      expanded={
                        selectedContainer === 'diff' &&
                        validationIncidents['schriftform']
                      }
                      label={t('ManagedOnlinedienstDetail.config.schriftform')}
                      after={
                        selectedContainer === 'edit' && (
                          <Button
                            variant="primary-text"
                            beforeIcon="edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSection('schriftform');
                            }}
                            size="sm"
                          >
                            {t('ManagedOnlinedienstDetail.editButton')}
                          </Button>
                        )
                      }
                    >
                      <DescriptionList>
                        <DiffDescriptionListItem
                          hasDivider={false}
                          key="schriftformerfordernis"
                          title={t(
                            'ManagedOnlinedienstDetail.auth.schriftformerfordernis'
                          )}
                          values={
                            selectedContainer === 'diff'
                              ? [
                                  [
                                    onlinedienst.activeContainer.authentication
                                      .schriftformNotRequired &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.schriftformNotRequiredValue'
                                      ),
                                    onlinedienst.activeContainer.authentication
                                      .withHybrid &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.hybrid'
                                      ),
                                    onlinedienst.activeContainer.authentication
                                      .withSignature &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.signature'
                                      ),
                                    onlinedienst.activeContainer.authentication
                                      .withEid &&
                                      t('ManagedOnlinedienstDetail.auth.eid'),
                                    onlinedienst.activeContainer.authentication
                                      .withSubstantiel &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.substantiell'
                                      )
                                  ]
                                    .filter(Boolean)
                                    .join(', ') || '-',
                                  [
                                    onlinedienst.editableContainer
                                      .authentication.schriftformNotRequired &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.schriftformNotRequiredValue'
                                      ),
                                    onlinedienst.editableContainer
                                      .authentication.withHybrid &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.hybrid'
                                      ),
                                    onlinedienst.editableContainer
                                      .authentication.withSignature &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.signature'
                                      ),
                                    onlinedienst.editableContainer
                                      .authentication.withEid &&
                                      t('ManagedOnlinedienstDetail.auth.eid'),
                                    onlinedienst.editableContainer
                                      .authentication.withSubstantiel &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.substantiell'
                                      )
                                  ]
                                    .filter(Boolean)
                                    .join(', ') || '-'
                                ]
                              : [
                                  [
                                    renderedOnlinedienst.authentication
                                      .schriftformNotRequired &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.schriftformNotRequiredValue'
                                      ),
                                    renderedOnlinedienst.authentication
                                      .withHybrid &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.hybrid'
                                      ),
                                    renderedOnlinedienst.authentication
                                      .withSignature &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.signature'
                                      ),
                                    renderedOnlinedienst.authentication
                                      .withEid &&
                                      t('ManagedOnlinedienstDetail.auth.eid'),
                                    renderedOnlinedienst.authentication
                                      .withSubstantiel &&
                                      t(
                                        'ManagedOnlinedienstDetail.auth.substantiell'
                                      )
                                  ]
                                    .filter(Boolean)
                                    .join(', ') || '-'
                                ]
                          }
                        />
                        <DiffDescriptionListItem
                          key="withEidLogin"
                          title={t('ManagedOnlinedienstDetail.auth.useLogin')}
                          values={
                            selectedContainer === 'diff'
                              ? [
                                  formatBool(
                                    onlinedienst.activeContainer.authentication
                                      .withEidLogin
                                  ),
                                  formatBool(
                                    onlinedienst.editableContainer
                                      .authentication.withEidLogin
                                  )
                                ]
                              : [
                                  formatBool(
                                    renderedOnlinedienst.authentication
                                      .withEidLogin
                                  )
                                ]
                          }
                        />
                      </DescriptionList>
                    </Accordion>
                  )}

                  {(selectedContainer !== 'diff' ||
                    validationIncidents['zustellweg']) && (
                    <Accordion
                      variant={mapOdIncidentsToColor(
                        validationIncidents.zustellweg,
                        selectedContainer
                      )}
                      expanded={
                        selectedContainer === 'diff' &&
                        validationIncidents['zustellweg']
                      }
                      label={t('ManagedOnlinedienstDetail.config.zustellweg')}
                    >
                      <Stack spacing="s">
                        {renderedOnlinedienst.zustellwege &&
                        renderedOnlinedienst.zustellwege.length > 0 ? (
                          <>
                            {renderedOnlinedienst.zustellwege.map(
                              (zustellweg, index) => (
                                <Card key={zustellweg.serviceId}>
                                  <Card.Content>
                                    <Stack
                                      direction="row"
                                      spacing="l"
                                      justifyContent="space-between"
                                      alignItems="center"
                                    >
                                      <Text strong>
                                        {t(
                                          'ManagedOnlinedienstDetail.config.zustellwegeInfo',
                                          { index: index + 1 }
                                        )}
                                      </Text>
                                      <Stack direction="row" spacing="xs">
                                        {selectedContainer === 'edit' && (
                                          <Button
                                            variant="primary-text"
                                            beforeIcon="edit"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              queryClient
                                                .fetchQuery({
                                                  queryKey: [
                                                    GovosApiRoutes.deliveryServices(
                                                      onlinedienst.startAppId
                                                    )
                                                  ]
                                                })
                                                .then(() => {
                                                  setSelectedSection(
                                                    'zustellweg'
                                                  );
                                                  setSelectedServiceIndex(
                                                    index
                                                  );
                                                });
                                            }}
                                            size="sm"
                                          >
                                            {t(
                                              'ManagedOnlinedienstDetail.editButton'
                                            )}
                                          </Button>
                                        )}
                                        {selectedContainer === 'edit' && (
                                          <Button
                                            variant="primary-text"
                                            beforeIcon="trashcan"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              deleteZustellweg({
                                                startAppId:
                                                  onlinedienst.startAppId,
                                                processId:
                                                  renderedOnlinedienst.processId,
                                                serviceIndex: index
                                              });
                                            }}
                                            size="sm"
                                            disabled={isDeletingZustellweg}
                                          >
                                            {t(
                                              'ManagedOnlinedienstDetail.deleteButton'
                                            )}
                                          </Button>
                                        )}
                                      </Stack>
                                    </Stack>
                                    <DescriptionList>
                                      <DiffDescriptionListItem
                                        key="zsService"
                                        title={t(
                                          'ManagedOnlinedienstDetail.zustellweg.service'
                                        )}
                                        values={
                                          selectedContainer === 'diff'
                                            ? [
                                                onlinedienst.activeContainer.zustellwege.at(
                                                  index
                                                )?.title || '-',
                                                onlinedienst.editableContainer.zustellwege.at(
                                                  index
                                                )?.title || '-'
                                              ]
                                            : [zustellweg.title || '-']
                                        }
                                      />
                                      <DiffDescriptionListItem
                                        key="zsServiceCode"
                                        title={t(
                                          'ManagedOnlinedienstDetail.zustellweg.serviceCode'
                                        )}
                                        values={
                                          selectedContainer === 'diff'
                                            ? [
                                                t(
                                                  `ManagedOnlinedienstDetail.zustellweg.serviceCode.${
                                                    onlinedienst.activeContainer.zustellwege.at(
                                                      index
                                                    )?.serviceCode || '-'
                                                  }`
                                                ),
                                                t(
                                                  `ManagedOnlinedienstDetail.zustellweg.serviceCode.${
                                                    onlinedienst.editableContainer.zustellwege.at(
                                                      index
                                                    )?.serviceCode || '-'
                                                  }`
                                                )
                                              ]
                                            : [
                                                t(
                                                  `ManagedOnlinedienstDetail.zustellweg.serviceCode.${zustellweg.serviceCode || '-'}`
                                                )
                                              ]
                                        }
                                      />
                                      <>
                                        {Object.entries(
                                          zustellweg.params || {}
                                        ).map(([key, value], index) => (
                                          <DiffDescriptionListItem
                                            key={key}
                                            title={t(
                                              `ManagedOnlinedienstDetail.zustellweg.params.${key}`
                                            )}
                                            values={diffMapper(
                                              key,
                                              value,
                                              onlinedienst.activeContainer.zustellwege.at(
                                                index
                                              )?.params,
                                              onlinedienst.editableContainer.zustellwege.at(
                                                index
                                              )?.params,
                                              onlinedienst.activeContainer.zustellwege.at(
                                                index
                                              )?.serviceCode ===
                                                onlinedienst.editableContainer.zustellwege.at(
                                                  index
                                                )?.serviceCode,
                                              false
                                            )}
                                          />
                                        ))}
                                      </>
                                    </DescriptionList>
                                  </Card.Content>
                                </Card>
                              )
                            )}
                          </>
                        ) : (
                          <Text size="s">
                            {t('ManagedOnlinedienstDetail.config.placeholder')}
                          </Text>
                        )}
                        {selectedContainer === 'edit' && (
                          <Button
                            variant="primary-text"
                            beforeIcon="add-outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              queryClient
                                .fetchQuery({
                                  queryKey: [
                                    GovosApiRoutes.deliveryServices(
                                      onlinedienst.startAppId
                                    )
                                  ]
                                })
                                .then(() => {
                                  setSelectedSection('zustellweg');
                                  setSelectedServiceIndex(-1);
                                });
                            }}
                            size="md"
                          >
                            {t(
                              'ManagedOnlinedienstDetail.zustellweg.addZustellweg'
                            )}
                          </Button>
                        )}
                      </Stack>
                    </Accordion>
                  )}
                  {onlinedienst.isVVAvailable &&
                    (selectedContainer !== 'diff' ||
                      validationIncidents['vv']) && (
                      <Accordion
                        variant={mapOdIncidentsToColor(
                          validationIncidents.vv,
                          selectedContainer
                        )}
                        expanded={
                          selectedContainer === 'diff' &&
                          validationIncidents['vv']
                        }
                        label={t('ManagedOnlinedienstDetail.config.processing')}
                        after={
                          selectedContainer === 'edit' && (
                            <Button
                              variant="primary-text"
                              beforeIcon="edit"
                              onClick={() => setSelectedSection('vv')}
                              size="sm"
                            >
                              {t('ManagedOnlinedienstDetail.editButton')}
                            </Button>
                          )
                        }
                      >
                        <DescriptionList>
                          <DiffDescriptionListItem
                            hasDivider={false}
                            key="vv.version"
                            title={t('ManagedOnlinedienstDetail.vv.version')}
                            values={
                              selectedContainer === 'diff'
                                ? []
                                : [onlinedienst.vvVersion || '-']
                            }
                          />
                        </DescriptionList>
                      </Accordion>
                    )}

                  {onlinedienst.isZufiAvailable &&
                    (selectedContainer !== 'diff' ||
                      validationIncidents['zustaendigkeitsfinder']) && (
                      <Accordion
                        variant={mapOdIncidentsToColor(
                          validationIncidents.zustaendigkeitsfinder,
                          selectedContainer
                        )}
                        expanded={
                          selectedContainer === 'diff' &&
                          validationIncidents['zustaendigkeitsfinder']
                        }
                        label={t(
                          'ManagedOnlinedienstDetail.config.zustaendigkeitsfinder'
                        )}
                        after={
                          selectedContainer === 'edit' && (
                            <Button
                              variant="primary-text"
                              beforeIcon="edit"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedSection('zustaendigkeitsfinder');
                              }}
                              size="sm"
                            >
                              {t('ManagedOnlinedienstDetail.editButton')}
                            </Button>
                          )
                        }
                      >
                        <DescriptionList>
                          <DiffDescriptionListItem
                            key="zustaendigkeitsfinder.veroeffentlichung"
                            hasDivider={false}
                            title={t(
                              'ManagedOnlinedienstDetail.zustaendigkeitsfinder.veroeffentlichung'
                            )}
                            values={
                              selectedContainer === 'diff'
                                ? [
                                    t(
                                      `ManagedOnlinedienstDetail.zustaendigkeitsfinder.veroeffentlichung.${onlinedienst.activeContainer?.zufi?.shouldRelease}`
                                    ),
                                    t(
                                      `ManagedOnlinedienstDetail.zustaendigkeitsfinder.veroeffentlichung.${onlinedienst.editableContainer?.zufi?.shouldRelease}`
                                    )
                                  ]
                                : [
                                    t(
                                      `ManagedOnlinedienstDetail.zustaendigkeitsfinder.veroeffentlichung.${renderedOnlinedienst.zufi?.shouldRelease ?? false}`
                                    )
                                  ]
                            }
                            valueRenderer={() => {
                              if (renderedOnlinedienst.zufi?.leistungName)
                                return (
                                  <Badge
                                    size="md"
                                    variant={
                                      renderedOnlinedienst.zufi?.shouldRelease
                                        ? 'success'
                                        : 'warning'
                                    }
                                  >
                                    {t(
                                      `ManagedOnlinedienstDetail.zustaendigkeitsfinder.veroeffentlichung.${renderedOnlinedienst.zufi?.shouldRelease ?? false}`
                                    )}
                                  </Badge>
                                );
                              return <div>-</div>;
                            }}
                          />
                          <DiffDescriptionListItem
                            key="zustaendigkeitsfinder.gebiet"
                            title={t(
                              'ManagedOnlinedienstDetail.zustaendigkeitsfinder.gebiet'
                            )}
                            values={
                              selectedContainer === 'diff'
                                ? [
                                    onlinedienst.activeContainer?.zufi
                                      ?.gebietName
                                      ? `${onlinedienst.activeContainer.zufi.gebietName} (${onlinedienst.activeContainer.zufi.gebietType})`
                                      : '-',
                                    onlinedienst.editableContainer.zufi
                                      ?.gebietName
                                      ? `${onlinedienst.editableContainer.zufi.gebietName} (${onlinedienst.editableContainer.zufi.gebietType})`
                                      : '-'
                                  ]
                                : [
                                    renderedOnlinedienst.zufi?.gebietName
                                      ? `${renderedOnlinedienst.zufi.gebietName} (${renderedOnlinedienst.zufi.gebietType})`
                                      : '-'
                                  ]
                            }
                          />
                          <DiffDescriptionListItem
                            key="zustaendigkeitsfinder.leistung"
                            title={t(
                              'ManagedOnlinedienstDetail.zustaendigkeitsfinder.leistung'
                            )}
                            values={
                              selectedContainer === 'diff'
                                ? [
                                    onlinedienst.activeContainer?.zufi
                                      ?.leistungName || '-',
                                    onlinedienst.editableContainer.zufi
                                      ?.leistungName || '-'
                                  ]
                                : [
                                    renderedOnlinedienst.zufi?.leistungName ||
                                      '-'
                                  ]
                            }
                          />
                          <DiffDescriptionListItem
                            key="zustaendigkeitsfinder.angezeigteLinkText"
                            title={t(
                              'ManagedOnlinedienstDetail.zustaendigkeitsfinder.angezeigteLinkText'
                            )}
                            values={
                              selectedContainer === 'diff'
                                ? [
                                    onlinedienst.activeContainer?.zufi
                                      ?.linkTitle || '-',
                                    onlinedienst.editableContainer.zufi
                                      ?.linkTitle || '-'
                                  ]
                                : [renderedOnlinedienst.zufi?.linkTitle || '-']
                            }
                          />
                        </DescriptionList>
                      </Accordion>
                    )}

                  {(selectedContainer !== 'diff' ||
                    validationIncidents['payment']) && (
                    <Accordion
                      variant={mapOdIncidentsToColor(
                        validationIncidents.payment,
                        selectedContainer
                      )}
                      expanded={
                        selectedContainer === 'diff' &&
                        validationIncidents['payment']
                      }
                      label={t('ManagedOnlinedienstDetail.config.payment')}
                      after={
                        selectedContainer === 'edit' && (
                          <Button
                            variant="primary-text"
                            beforeIcon="edit"
                            onClick={() => {
                              queryClient
                                .fetchQuery({
                                  queryKey: [
                                    GovosApiRoutes.paymentServices(
                                      onlinedienst.startAppId
                                    )
                                  ]
                                })
                                .then(() => {
                                  setSelectedSection('payment');
                                });
                            }}
                            size="sm"
                          >
                            {t('ManagedOnlinedienstDetail.editButton')}
                          </Button>
                        )
                      }
                    >
                      <Stack spacing="l">
                        <DescriptionList>
                          <DiffDescriptionListItem
                            hasDivider={false}
                            key="service"
                            title={t(
                              'ManagedOnlinedienstDetail.payment.service'
                            )}
                            values={
                              selectedContainer === 'diff'
                                ? [
                                    onlinedienst.activeContainer.payment
                                      .paymentService?.title || '-',
                                    onlinedienst.editableContainer.payment
                                      .paymentService?.title || '-'
                                  ]
                                : [
                                    renderedOnlinedienst.payment.paymentService
                                      ?.title || '-'
                                  ]
                            }
                          />
                          <DiffDescriptionListItem
                            key="policy"
                            title={t(
                              'ManagedOnlinedienstDetail.payment.policy'
                            )}
                            values={
                              selectedContainer === 'diff'
                                ? [
                                    t(
                                      `ManagedOnlinedienstDetail.payment.policy.${onlinedienst.activeContainer.payment.policy}`
                                    ),
                                    t(
                                      `ManagedOnlinedienstDetail.payment.policy.${onlinedienst.editableContainer.payment.policy}`
                                    )
                                  ]
                                : [
                                    t(
                                      `ManagedOnlinedienstDetail.payment.policy.${renderedOnlinedienst.payment.policy}`
                                    )
                                  ]
                            }
                          />
                          <>
                            {selectedContainer !== 'diff' &&
                              Object.entries(
                                renderedOnlinedienst.payment.paymentService
                                  ?.params || {}
                              ).map(([key, value]) => (
                                <DiffDescriptionListItem
                                  key={key}
                                  title={t(
                                    `ManagedOnlinedienstDetail.payment.params.${key}`
                                  )}
                                  values={[value]}
                                />
                              ))}
                          </>
                          <>
                            {selectedContainer === 'diff' &&
                              Object.entries(
                                onlinedienst.editableContainer.payment
                                  .paymentService?.params || {}
                              ).map(([key, value]) => (
                                <DiffDescriptionListItem
                                  key={key}
                                  title={t(
                                    `ManagedOnlinedienstDetail.payment.params.${key}`
                                  )}
                                  values={diffMapper(
                                    key,
                                    value,
                                    onlinedienst.activeContainer.payment
                                      .paymentService?.params,
                                    onlinedienst.editableContainer.payment
                                      .paymentService?.params,
                                    onlinedienst.activeContainer.payment
                                      .paymentService?.code ===
                                      onlinedienst.editableContainer.payment
                                        .paymentService?.code,
                                    false
                                  )}
                                />
                              ))}
                            {selectedContainer === 'diff' &&
                              Object.entries(
                                onlinedienst.activeContainer.payment
                                  .paymentService?.params || {}
                              ).map(([key, value]) => (
                                <DiffDescriptionListItem
                                  key={key}
                                  title={t(
                                    `ManagedOnlinedienstDetail.payment.params.${key}`
                                  )}
                                  values={diffMapper(
                                    key,
                                    value,
                                    onlinedienst.activeContainer.payment
                                      .paymentService?.params,
                                    onlinedienst.editableContainer.payment
                                      .paymentService?.params,
                                    onlinedienst.activeContainer.payment
                                      .paymentService?.code ===
                                      onlinedienst.editableContainer.payment
                                        .paymentService?.code,
                                    true
                                  )}
                                />
                              ))}
                          </>
                        </DescriptionList>
                      </Stack>
                    </Accordion>
                  )}
                </OdmAccordionGroup>
              </div>
            </Stack>
          </div>
          {selectedSection === 'payment' && (
            <EditPaymentConfigModal
              startAppId={onlinedienst.startAppId}
              processId={onlinedienst.editableContainer.processId}
              payment={onlinedienst.editableContainer.payment}
              isOpen
              onClose={handleCloseModal}
            />
          )}
          {selectedSection === 'verfahren' && onlinedienst && (
            <EditOnlinedienstMetadatenModal
              isOpen
              onlinedienst={onlinedienst}
              onClose={handleCloseModal}
            />
          )}
          {selectedSection === 'auth' && onlinedienst && (
            <EditAuthenticationOptionsModal
              isOpen
              startAppId={onlinedienst.startAppId}
              processId={onlinedienst.editableContainer.processId}
              authentication={onlinedienst.editableContainer.authentication}
              onClose={handleCloseModal}
            />
          )}
          {selectedSection === 'schriftform' && onlinedienst && (
            <EditSchriftformConfigModal
              isOpen
              startAppId={onlinedienst.startAppId}
              processId={onlinedienst.editableContainer.processId}
              authentication={onlinedienst.editableContainer.authentication}
              onClose={handleCloseModal}
            />
          )}
          {selectedSection === 'zustellweg' &&
            onlinedienst &&
            selectedServiceIndex !== null && (
              <EditZustellwegConfigModal
                isOpen
                startAppId={onlinedienst.startAppId}
                processId={onlinedienst.editableContainer.processId}
                zustellwege={onlinedienst.editableContainer.zustellwege}
                onClose={handleCloseModal}
                serviceIndex={selectedServiceIndex}
                onSuccess={() => {
                  handleCloseModal();
                  void queryClient.refetchQueries({
                    queryKey: [
                      GovosApiRoutes.configuredOnlineDienst(processId ?? '')
                    ]
                  });
                  void queryClient.invalidateQueries({
                    predicate: invalidatePaginationConfiguredOnlinediensteCache
                  });
                }}
              />
            )}
          {selectedSection === 'zustaendigkeitsfinder' && onlinedienst && (
            <EditZuFiConfigModal
              isOpen
              onClose={handleCloseModal}
              onlinedienst={onlinedienst}
            />
          )}
          {selectedSection === 'vv' && (
            <EditVerarbeitungsverzModal
              isOpen
              onClose={handleCloseModal}
              onlinedienst={onlinedienst}
            />
          )}
          {selectedSection === 'ueberschreibbareFelder' &&
            selectedFeld &&
            onlinedienst && (
              <EditUeberschreibbareFelderModal
                isOpen
                onClose={handleCloseModal}
                feldId={selectedFeld.feldId}
                feldName={selectedFeld.feldName}
                startAppId={onlinedienst.startAppId}
                processId={onlinedienst.editableContainer.processId}
                onShowOriginalValues={() => {
                  handleCloseModal();
                  setShouldShowOriginalValues(true);
                }}
              />
            )}
          {shouldShowOriginalValues && selectedFeld && onlinedienst && (
            <OriginalValuesModal
              isOpen
              onClose={() => setShouldShowOriginalValues(false)}
              feldId={selectedFeld.feldId}
              feldName={selectedFeld.feldName}
              startAppId={onlinedienst.startAppId}
              processId={onlinedienst.editableContainer.processId}
            />
          )}
        </div>
      </Stack>
    </div>
  );
}
